import type {
  NodeChangeStatus,
  PlaybookCriterionNode,
} from "@repo/app-types/entities/playbook";
import type { FeedbackWithCurrentGrade } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { Size } from "@repo/ui/helpers/helpers";
import type { FC } from "react";
import { TreeItem, TreeItemContent } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { treeItem } from "../tree/treeStyles.js";
import { serializeAssessmentTreeItemKey } from "./assessmentTreeHelpers.js";
import { calculateFeedbackAccuracyWithCurrentGrades } from "./feedbackAccuracyHelpers.js";
import { GradingFeedbackCircle } from "./gradingFeedbackCircle.js";
import { NodeChangeDot } from "./nodeChangeDot.js";

export interface AssessmentCriteriaTreeItemProps {
  criteria: PlaybookCriterionNode;
  criteriaIndex: number;
  level?: number;
  isLastItemAtLevel?: boolean;
  onAction?: () => void;
  className?: string;
  feedback: readonly FeedbackWithCurrentGrade[];
  /** The change status of this node compared to the live version */
  changeStatus?: NodeChangeStatus;
  /** Whether the agent has a saved proposal for this criterion — surfaces an indigo agent-suggestion dot. */
  hasSuggestion?: boolean;
  /**
   * Ghost mode for agent-proposed new criteria that aren't in the tree yet —
   * uses the `ghost-criterion` tree key, renders the proposal name without
   * an index prefix, and falls back to "Proposed criterion".
   */
  isGhost?: boolean;
}

/** Renders a criteria tree item in the assessment tree UI. */
export const AssessmentCriteriaTreeItem: FC<
  AssessmentCriteriaTreeItemProps
> = ({
  criteria,
  criteriaIndex,
  level = 0,
  isLastItemAtLevel = false,
  onAction,
  className,
  feedback,
  changeStatus,
  hasSuggestion = false,
  isGhost = false,
}) => {
  const treeItemId = serializeAssessmentTreeItemKey({
    type: isGhost ? "ghost-criterion" : "criteria",
    id: criteria.id,
  });
  const textValue = isGhost
    ? criteria.name.trim() || "Proposed criterion"
    : criteria.name || `${criteriaIndex + 1} Untitled criteria`;

  // Calculate interior padding based on hierarchy level
  const getInteriorPaddingClass = () => {
    switch (level) {
      case 0:
        return "px-4";
      case 1:
        return "pl-6 pr-4"; // Hierarchy indicator + space-x-4 provides the indentation
      case 2:
        return "pl-12 pr-4"; // Criteria under sub-sections need more indentation
      case 3:
        return "pl-16 pr-4"; // Even deeper nesting
      default:
        return "pl-20 pr-4"; // Max indentation for very deep nesting
    }
  };

  const accuracy = calculateFeedbackAccuracyWithCurrentGrades(
    criteria.id,
    feedback,
  );

  return (
    <TreeItem
      id={treeItemId}
      textValue={textValue}
      onAction={onAction}
      className={treeItem({
        className,
        isLastItemAtLevel,
      })}
    >
      <TreeItemContent>
        <div
          className={twMerge(
            "flex items-center space-x-4 py-2",
            getInteriorPaddingClass(),
          )}
        >
          {/* Hierarchy indicator */}
          {level > 0 && (
            <div className="h-3 w-3 shrink-0 rounded-bl-md border-b-2 border-l-2 border-border-strong" />
          )}

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            <span className="truncate text-sm font-medium text-foreground">
              {isGhost ? (
                criteria.name.trim() || (
                  <span className="text-muted-foreground">
                    Proposed criterion
                  </span>
                )
              ) : criteria.name ? (
                `${criteriaIndex + 1}. ${criteria.name}`
              ) : (
                <span className="text-muted-foreground">
                  {criteriaIndex + 1}. Untitled criteria
                </span>
              )}
            </span>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-3">
            {accuracy && accuracy.total > 0 && (
              <GradingFeedbackCircle
                correctCount={accuracy.correct}
                closeCount={accuracy.close}
                incorrectCount={accuracy.incorrect}
                size={Size.SMALL}
              />
            )}
            <NodeChangeDot
              entity="criterion"
              variant={hasSuggestion ? "suggestion" : "draft"}
              isVisible={hasSuggestion || changeStatus !== undefined}
            />
          </div>
        </div>
      </TreeItemContent>
    </TreeItem>
  );
};
